import { Link } from "react-router";
import { adminPaths } from "../AdminApp";
import { ReactNode } from "react";
import { SignOutButton, useUser } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { AdminApi } from "../api/admin-api";

interface AdminLayoutProps {
  title: string;
  description: string;
  breadcrumb?: string;
  children: ReactNode;
  headerAction?: ReactNode;
}

export default function AdminLayout({
  title,
  description,
  breadcrumb,
  children,
  headerAction,
}: AdminLayoutProps) {
  const { user } = useUser();

  // Check user's roles to determine if they're a superuser
  const { data: userRoles } = useQuery({
    queryKey: ["userSelfRoles"],
    queryFn: AdminApi.getUserSelfRoles,
    retry: false, // Don't retry on failure
  });

  const isSuperuser =
    userRoles?.roles.includes("amplifiles-superuser") ?? false;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Top bar with user info and logout */}
        <div className="flex justify-end mb-4">
          <div className="flex items-center gap-3 text-sm">
            {user?.primaryEmailAddress?.emailAddress && (
              <span className="text-gray-600">
                {user.primaryEmailAddress.emailAddress}
              </span>
            )}
            <SignOutButton>
              <button className="px-3 py-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors">
                Sign out
              </button>
            </SignOutButton>
          </div>
        </div>

        {/* Superuser Warning */}
        {userRoles && !isSuperuser && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center">
              <div className="text-yellow-600 text-lg mr-2">⚠️</div>
              <p className="text-yellow-800 text-sm font-medium">
                Not a superuser - functionality limited
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
              <p className="mt-2 text-gray-600">{description}</p>
            </div>
            {headerAction && <div>{headerAction}</div>}
          </div>
        </div>

        {/* Breadcrumb */}
        {breadcrumb && (
          <div className="mb-6">
            <nav className="flex text-sm">
              <Link
                to={adminPaths.dashboard()}
                className="text-blue-600 hover:text-blue-700"
              >
                Dashboard
              </Link>
              <span className="mx-2 text-gray-500">/</span>
              <span className="text-gray-700">{breadcrumb}</span>
            </nav>
          </div>
        )}

        {/* Main Content */}
        {children}
      </div>
    </div>
  );
}
